/* global $ */
import { Component, Fragment } from 'preact';
import { lazy, Suspense } from 'preact/compat';
import { Localizer, Text, withText } from 'preact-i18n';
import dayjs from 'dayjs';
import { initTest } from '../utils/abtest';
import { computeHasPaymentMethodsInTop } from '../utils/visibility';

// Lazy-loaded components for code splitting (migrated from async! syntax)
const Datepicker = lazy(() => import('./DatePicker'));
const BannerBranding = lazy(() => import('./BannerBranding'));
const RoundSwitch = lazy(() => import('./RoundSwitch'));
import validate from '../utils/validate';
import resultsUrl from '../utils/resultsUrl';
import decodePassengers, { getPassengersSelectorInitialState } from '../utils/decodePassengers';
import searchFromQuery from '../utils/searchFromQuery';
import validateDate from '../utils/validateDate';
import stringToDate from '../utils/stringToDate';
import createUrl from '../utils/createUrl';
import gtmSetDataLayer from '../utils/gtmSetDataLayer';
import isPlaceReady from '../utils/isPlaceReady';
import mixpanelTracker from '../utils/mixpanelTracker';
import WidgetTitle from './WidgetTitle';
import '../style.scss';
import getCalendarRanges from '../utils/getCalendarRanges';
import PaymentMethods from './PaymentMethods';
import WhatsappCta from './WhatsappCta';
import WhatsappBubble from './WhatsappBubble';
import { getRecentSearches, saveRecentSearches } from '../utils/recentTrips';
import ReverseRoutesButton from './ReverseRoutesButton';
import { camelizeKeys } from 'humps';
import { withUserPreferences } from '../context/userPreferences/';
import { selectIcon } from '../templates/place';
import http from '../services/http/';
import { withWidgetConfig } from '../context/WidgetConfig';
import { withGrowthBookFeatures } from '../context/GrowthBook';
import PassengersSelector from './PassengersSelector';
import UnifiedDatePicker from './UnifiedDatePicker';
import {
  buildMobilityUrlFromSearch,
  mobilitySearchHotelsClickTracker,
  openMobilityLeaveBehind,
  shouldOpenMobilityLeaveBehind,
} from '../utils/mobility';

const MOBILITY_LABEL_COLORS = {
  black: '#232323',
  white: '#ffffff',
};

const getInterestInSearchEventHandler = (section, additionalProps = {}) => () => {
  mixpanelTracker.trackInterestInSearchEvent({
    section,
    ...additionalProps,
  });
};

class Search extends Component {
  state = {
    errors: {},
    passengers: {},
    tripType: 'oneWay',
    hybridTrip: 'none', // none, departure, return, both
    usingNearbyTerminal: false,
    loadingNearbyTerminal: false,
    autoOpenCalendar: false,
    destinationResetKey: 0,
    clearedDestinationSlug: false,
  };

  fieldClasses = {
    origin: 'input.origin',
    destination: 'input.destination',
    departureDate: 'input.departureDate',
    returnDate: 'input.returnDate',
  };

  constructor(props) {
    super(props);

    this.debounce = null;
    this.showPaymentMethods = true;
    this.showDiscountsAB = true;
    this.showTextPromAB = true;
    this.usingNearbyTerminal = false;

    this.onInputChange = this.onInputChange.bind(this);
    this.onChangeAutocomplete = this.onChangeAutocomplete.bind(this);
    this.onChangePassengers = this.onChangePassengers.bind(this);
    this.onChangeDepartureDate = this.onChangeDepartureDate.bind(this);
    this.onChangeReturnDate = this.onChangeReturnDate.bind(this);
    this.setGtmProperties = this.setGtmProperties.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onChangeDepartureToOpenTicket = this.onChangeDepartureToOpenTicket.bind(this);
    this.onChangeReturnToOpenTicket = this.onChangeReturnToOpenTicket.bind(this);
    this.onRemoveReturnOpenTicket = this.onRemoveReturnOpenTicket.bind(this);
    this.onRemoveDepartureOpenTicket = this.onRemoveDepartureOpenTicket.bind(this);
    this.setDeparturePicker = this.setDeparturePicker.bind(this);
    this.onDatePickerCreated = this.onDatePickerCreated.bind(this);
    this.reversePlaces = this.reversePlaces.bind(this);
    this.fetchNearTerminals = this.fetchNearTerminals.bind(this);
    this.onFocusSearchButton = this.onFocusSearchButton.bind(this);
    this.onNoSearchResults = this.onNoSearchResults.bind(this);
    this.onInputFocus = this.onInputFocus.bind(this);
    this.configFlagsOnChangeAutocomplete = this.configFlagsOnChangeAutocomplete.bind(this);
    this.wasAutoPlaceModified = this.wasAutoPlaceModified.bind(this);
    this.didInitialOriginChanged = this.didInitialOriginChanged.bind(this);
    this.isUsingRecentSearch = this.isUsingRecentSearch.bind(this);
    this.isUsingPopularDestinations = this.isUsingPopularDestinations.bind(this);
    this.onDepartureDateSelected = this.onDepartureDateSelected.bind(this);
    this.setPreferredTerminalsFlag = this.setPreferredTerminalsFlag.bind(this);
    this.onResetPassengers = this.onResetPassengers.bind(this);
    this.autoOpenPassengersSelector = this.autoOpenPassengersSelector.bind(this);
    this.onDateContinue = this.onDateContinue.bind(this);
    this.handleOnClickSubmit = this.handleOnClickSubmit.bind(this);
    this.onMobilityCheckboxChange = this.onMobilityCheckboxChange.bind(this);
    this.clearReturnDate = this.clearReturnDate.bind(this);
    this.clearDepartureDate = this.clearDepartureDate.bind(this);
    this.validatePendingDates = this.validatePendingDates.bind(this);
    this.validateAndFormatDate = this.validateAndFormatDate.bind(this);
    this.updateDatePickersAfterChange = this.updateDatePickersAfterChange.bind(this);
    this.updateDatepickerRangesAfterValidation = this.updateDatepickerRangesAfterValidation.bind(
      this
    );
    this.updateInputValuesAfterValidation = this.updateInputValuesAfterValidation.bind(this);

    this.formatDateForDisplay = this.formatDateForDisplay.bind(this);

    // Indicates if a input has been clicked
    this.inputChanged = false;
    this.inputFocused = false;
    this.placeSelected = false;

    this.geoLocationAsked = false;
    this.usingGeoOrigin = false;
    this.geoOriginShowed = false;
    this.departureCalendarOpened = false;
    this.initialOriginChanged = false;

    this.preferredTerminalsFlags = {
      usingOrigin: false,
      usingDestination: false,
      usingRecentSearch: false,
      showed: false,
    };

    this.popularDestinationsUsed = false;
    this.calendarButtonUsed = null;
    this.compactDateUsed = false;
    this.bothPlacesWereSelected = false;
  }

  componentDidMount() {
    this.setInitialState();
    const { config } = this.props;
    const { optInReturn, calendarOpen } = config;

    this.initAbTests();

    if (optInReturn && !calendarOpen) {
      const { tripType } = this.state;

      if (tripType === 'oneWay') {
        this.$get('returnDate').hide();
        // eslint-disable-next-line react/no-did-mount-set-state
        this.setState({ returnDate: null });
      }
    }

    this.emitSearchLoaded();
  }

  componentDidUpdate(prevProps, { tripType: prevTripType }) {
    const { config, growthbookFeatures } = this.props;
    const { optInReturn, calendarOpen } = config;
    const { tripType } = this.state;

    this.autoSelectionManagement();

    if (growthbookFeatures?.search_passengers && !prevProps.growthbookFeatures?.search_passengers) {
      const passengersState = this.getInitialPassengersState();
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        passengers: passengersState,
      });
    }

    if (!optInReturn || tripType === prevTripType) return;

    if (tripType === 'round' && !calendarOpen) {
      this.$get('returnDate').show();
    } else if (tripType === 'oneWay') {
      this.$get('returnDate').hide();
    }
  }

  componentWillUnmount() {
    clearTimeout(this.debounce);
  }

  /**
   *
   * @param {String} section - The section of the search that is being focused
   * @param {*} additionalProps - Additional properties to be sent to mixpanel
   * @returns
   */
  onInputFocus = (section, additionalProps = {}) => () => {
    /**
     * This variable is set true to indicate that the user has focused some input,
     * in this way we can avoid to autofill the input and override what the user has wrote
     */
    this.inputFocused = true;
    getInterestInSearchEventHandler(section, additionalProps)();

    // Scroll window to position input at 150px from top
    // Use setTimeout to avoid race conditions with dropdown initialization
    if (section === 'Origin' || section === 'Destination') {
      this.scrollToInput(section.toLowerCase());
    }
  };

  onDatePickerCreated(way) {
    const { config } = this.props;
    const {
      departureDate: initialDepartureDate,
      returnDate: initialReturnDate,
      maxDaysSearch,
    } = config;
    const { departureDate } = this.state;

    if (way === 'return' && initialDepartureDate) {
      this.onChangeDepartureDate(departureDate);
    }
    if (way === 'departure' && initialReturnDate) {
      const validatedReturnDate = this.validateAndFormatDate(initialReturnDate, maxDaysSearch);
      this.onChangeReturnDate(validatedReturnDate || '');
    }
  }

  /**
   * Smoothly scrolls to position the input field optimally in the viewport
   * Uses setTimeout to avoid race conditions with dropdown initialization
   * @param {string} field - The field to scroll to ('origin' or 'destination')
   */
  scrollToInput(field) {
    setTimeout(() => {
      const $input = this.$get(field);

      if ($input.length > 0) {
        const inputElement = $input[0];
        const inputRect = inputElement.getBoundingClientRect();
        const currentScrollY = window.pageYOffset || document.documentElement.scrollTop;
        const targetScrollY = currentScrollY + inputRect.top - 100;

        // Only scroll if there's a meaningful distance to prevent unnecessary animation
        if (Math.abs(inputRect.top - 100) > 10) {
          window.scrollTo({
            top: targetScrollY,
            behavior: 'smooth',
          });
        }
      }
    }, 100);
  }

  /**
   * Decides which auto-selection feature should be triggered
   * order: preferences > geolocation origin
   */
  autoSelectionManagement() {
    const { userPreferences, config } = this.props;
    const { destinationFirst } = config;

    // Disable auto-selection in destination-first experimental group
    if (destinationFirst) return;

    if (!userPreferences.requested || config.origin) return;

    if (!this.inputChanged && !this.preferredTerminalsSetup && userPreferences.originPlace) {
      this.preferredTerminalsSetup = true;
      this.setUpPreferredTerminals();
      return;
    }

    if (!this.geoLocationAsked) {
      this.geoLocationAsked = true;
      this.getGeolocation();
      return;
    }
  }

  /**
   * Set up the terminal preferred by the user,
   * If the user has a preferred terminal, the places
   * that matches with those values will be set as the origin and destination
   */
  setUpPreferredTerminals() {
    const { userPreferences } = this.props;
    const { originPlace, destinationPlace } = userPreferences;

    if (originPlace && destinationPlace) {
      /**
       * This class variables are set true to indicate that at the moment,
       * the preferred places have been showed and used by the user.
       */
      this.preferredTerminalsFlags = {
        usingOrigin: true,
        usingDestination: true,
        showed: true,
      };

      if (!this.placeSelected) {
        this.setState({
          preferredOrigin: originPlace,
          preferredDestination: destinationPlace,
        });
      }
    }
  }

  /**
   * Gets the current geolocation of the user and sets it as the origin
   */
  getGeolocation() {
    const { config } = this.props;
    const { destinationFirst } = config;

    // Disable geolocation in destination-first experimental group
    if (destinationFirst) return;

    const { origin, geolocationOrigin, orderByGeoLocation, showNearestTerminal } = config;
    const { coords } = this.state;
    if (coords || origin || (!geolocationOrigin && !orderByGeoLocation && !showNearestTerminal))
      return;

    const GEOLOCATION_TRACK_EVENT = 'Geolocation Requested';
    this.orderByGeoLocationActive = orderByGeoLocation;

    navigator.geolocation.getCurrentPosition(
      geolocation => {
        const { latitude: lat, longitude: long } = geolocation.coords;
        const coords = {
          lat,
          long,
        };
        mixpanelTracker.trackEvent(GEOLOCATION_TRACK_EVENT, { geolocationAccepted: true });
        this.setState({ coords }, () => {
          if (
            (geolocationOrigin && !this.preferredTerminalsFlags.showed && !this.inputChanged) ||
            showNearestTerminal
          ) {
            this.fetchNearTerminals(coords);
          }
        });
      },
      () => {
        mixpanelTracker.trackEvent(GEOLOCATION_TRACK_EVENT, { geolocationAccepted: false });
      }
    );
  }

  fetchNearTerminals(coords) {
    const { config } = this.props;
    const { destinationFirst } = config;

    // Disable near terminals in destination-first experimental group
    if (destinationFirst) return;

    const { origin, geolocationOrigin, showNearestTerminal } = config;
    if (origin) return;
    const { lat, long } = coords;
    const sourceUrl = this.getAutoCompleteUrl().href;
    const url = `${sourceUrl}/near-to-user?lat=${lat}&long=${long}`;
    http.get(url).then(data => {
      if (data && data.length > 0) {
        const place = data[0];
        // A flag is set true to know that the origin based on geolocation is showed
        if (!this.preferredTerminalsFlags.showed) {
          this.geoOriginShowed = true;
          this.usingGeoOrigin = true;
        }
        if (
          !this.inputChanged &&
          !this.placeSelected &&
          ((geolocationOrigin && !this.preferredTerminalsFlags.showed) || showNearestTerminal)
        )
          this.setState({
            geoLocationOrigin: camelizeKeys(place),
            initialOriginSlug: place.slug,
          });
      }
    });
  }

  /**
   * Handle the change of the origin or departure place. Event is triggered after the user types.
   * @param {string} field - Field origin or destination
   * @param {string} input - Input value
   */
  onInputChange(field, input) {
    this.inputChanged = true;
    clearTimeout(this.debounce);
    // If the input is empty, we don't want to trigger anything
    if (!input || input === '') return;

    this.debounce = setTimeout(() => {
      this.setState(prevState => ({
        ...prevState,
        [`${field}SearchAttempts`]: [...prevState[`${field}SearchAttempts`], input],
      }));
    }, 2000);
  }

  /**
   * Checks if the place was change because the user selected or typed a new place
   * @param {Object} autoSelectedPlace - Place that was auto selected
   * @param {Object} newPlace - Place that was selected by the user
   * @returns {Boolean}
   */
  wasAutoPlaceModified({ autSelectedPlace, newPlace }) {
    const inputModified = this.inputChanged || this.inputFocused;
    return autSelectedPlace?.id !== newPlace.id && inputModified;
  }

  /**
   * Checks if the preferred terminals where changed by the user
   * @param {Object} place - Place selected
   * @param {String} field - Field origin or destination
   */
  didPreferredChanged = ({ place, field }) => {
    const { growthbookFeatures } = this.props;
    const { preferredOrigin, preferredDestination } = this.state;
    if (!this.preferredTerminalsFlags.showed || growthbookFeatures.preferredTerminalsDesign) return;

    const isRecentTrip = field === 'recentTrip';

    const originWasChanged = this.wasAutoPlaceModified({
      autSelectedPlace: preferredOrigin,
      newPlace: place,
    });

    const destinationWasChanged = this.wasAutoPlaceModified({
      autSelectedPlace: preferredDestination,
      newPlace: place,
    });

    if (isRecentTrip || (field === 'origin' && (originWasChanged || this.inputChanged))) {
      // If the origin was changed, then the preferred terminals are not used
      if (originWasChanged || this.inputChanged) {
        this.preferredTerminalsFlags.usingOrigin = false;
        this.initialOriginChanged = true;
      }
    }
    if (isRecentTrip || (field === 'destination' && (destinationWasChanged || this.inputChanged))) {
      // If the destination was changed, then the preferred terminals are not used
      this.preferredTerminalsFlags.usingDestination = false;
    }
  };

  /**
   * Checks if the geolocation origin where changed by the user
   * @param {Object} place - Place selected
   * @param {String} field - Field origin or destination
   */
  didGeoOriginChanged = ({ place, field }) => {
    const { geoLocationOrigin } = this.state;
    const geoOriginWasChanged = this.wasAutoPlaceModified({
      autSelectedPlace: geoLocationOrigin,
      newPlace: place,
    });

    if (field === 'origin') {
      // If the origin was changed, then the geolocation origin is not used
      if (geoOriginWasChanged || this.inputChanged) {
        this.usingGeoOrigin = false;
        this.initialOriginChanged = this.geoOriginShowed && true;
      }
    }
  };

  /**
   * Checks if the nearby terminal was used by the user
   * @param {Object} place - Place selected
   * @param {String} field - Field origin or destination
   */
  didSelectedNearbyPlace({ place, field }) {
    const { geoLocationOrigin } = this.state;
    if (field === 'origin') {
      if (place.id === geoLocationOrigin?.id) {
        this.usingNearbyTerminal = true;
      } else {
        this.usingNearbyTerminal = false;
      }
    }
  }

  /**
   * This functions is used to make now the destination input if the initial origin was changed
   * and be able to do some logic with it.
   * @returns {Boolean} - Indicates if the initial origin was changed by the user
   */
  didInitialOriginChanged() {
    return this.initialOriginChanged;
  }

  /**
   * Checks if a preferred terminal was used
   * @param {Object} params - function parameters
   * @param {Object} place - Place object
   */
  setPreferredTerminalsFlag() {
    const { growthbookFeatures } = this.props;
    const { origin, destination } = this.state;

    if (!growthbookFeatures.preferred_terminals_design) return;

    this.preferredTerminalsFlags.usingOrigin = Boolean(origin?.preferred);
    this.preferredTerminalsFlags.usingDestination = Boolean(destination?.preferred);
  }

  /**
   * Executes functions to configure the flags that are used to know if the user has used some features
   * and be able to reflect this with the analytics events or control the behavior of the widget
   * @param {Object} place - Place selected
   * @param {String} field - Field origin or destination
   */
  configFlagsOnChangeAutocomplete(field, place) {
    this.didPreferredChanged({ field, place });
    this.didGeoOriginChanged({ field, place });
    this.didSelectedNearbyPlace({ field, place });
    this.isUsingRecentSearch({ place });
    this.isUsingPopularDestinations({ field, place });
  }

  /**
   * Checks if a popular destination was used
   * @param {Object} params - function parameters
   * @param {String} params.field - Field origin or destination
   * @param {Object} params.place - Place selected
   * @returns
   */
  isUsingPopularDestinations({ field, place }) {
    if (field !== 'destination') return;
    this.popularDestinationsUsed = Boolean(place?.isPopularDestination);
  }

  /**
   * Checks is a recent search is used
   * @param {Object} params = function parameters
   * @param {String} params.field - Field origin or destination
   * @param {Object} params.place - Place selected
   */
  isUsingRecentSearch({ place }) {
    const { origin, destination } = place;
    if (origin?.recentSearch && destination?.recentSearch) {
      this.preferredTerminalsFlags.usingRecentSearch = true;
    } else {
      this.preferredTerminalsFlags.usingRecentSearch = false;
    }
  }

  /**
   * This function auto opens the calendar
   */
  openDepartureCalendar() {
    const { departureDate, origin, destination } = this.state;
    const { config } = this.props;
    const { destinationFirst } = config;

    if (!this.inputFocused || departureDate) return;

    // In destination-first: only open when BOTH are selected for the first time
    if (destinationFirst) {
      const bothSelected = origin?.slug && destination?.slug;
      const isCompletingPair = bothSelected && !this.bothPlacesWereSelected;

      if (!isCompletingPair) {
        return;
      }

      // Mark that both were selected
      this.bothPlacesWereSelected = true;
    }

    this.setState({
      autoOpenCalendar: 'departure',
    });
  }

  /**
   * This function auto opens the passengers selector
   */
  autoOpenPassengersSelector() {
    const {
      config: { passengersForSelector, departureDate },
      growthbookFeatures,
    } = this.props;
    if (!departureDate && passengersForSelector?.length && growthbookFeatures?.search_passengers) {
      this.setState({
        autoOpenPassengersSelector: true,
      });
    }
  }

  onPassengersDropdownClose = () => {
    this.setState({ autoOpenPassengersSelector: false });
  };

  onDateContinue() {
    const { config, growthbookFeatures } = this.props;
    const showPassengersDropdown =
      growthbookFeatures?.search_passengers && config.passengersDropdown;

    if (showPassengersDropdown) {
      this.autoOpenPassengersSelector();
    } else {
      this.handleOnClickSubmit();
    }
  }

  /**
   * Handle the change of the origin or departure place. Event is triggered after the user selects a place
   * @param {string} field - Field origin or destination
   * @param {Object} place - Place selected
   */
  onChangeAutocomplete(field, place) {
    this.configFlagsOnChangeAutocomplete(field, place);

    const { origin, destination } = place;
    const {
      errors,
      origin: currentOrigin,
      destination: currentDestination,
      destinationResetKey,
    } = this.state;
    const { updateMaxDaysSearch } = this.props.config;
    const { config } = this.props;
    const { destinationFirst } = config;

    this.placeSelected = Boolean(place.id || origin);

    /**
     * When selecting a recent search or a recommended route, origin and destination
     * will be passed instead of just one place.
     */
    if (origin && destination) {
      // CRITICAL: Reset flag to allow calendar opening
      if (destinationFirst) {
        this.bothPlacesWereSelected = false;
      }

      const destinationMaxDaysSearch = destination.meta?.maxDaysSearch || null;

      // Combine all states in a single call
      // CRITICAL: First reset autoOpenCalendar to false
      this.setState(
        prevState => ({
          origin,
          destination,
          autoOpenCalendar: false, // Reset first
          errors: {
            ...prevState.errors,
            origin: '',
            destination: '',
          },
        }),
        () => {
          this.setGtmProperties();
          // CRITICAL: Execute after state updates
          // Use setTimeout to ensure the change from false -> 'departure' is detected
          setTimeout(() => {
            this.openDepartureCalendar();
          }, 0);
        }
      );

      updateMaxDaysSearch(destinationMaxDaysSearch);
      this.validatePendingDates(destinationMaxDaysSearch);
    } else {
      // Update base state
      const newState = { [field]: place };

      // If it's destination, also update clearedDestinationSlug
      if (field === 'destination' && Object.keys(place).length) {
        newState.clearedDestinationSlug = false;
      }

      this.setState(newState, () => this.setGtmProperties());

      if (field === 'origin' && Object.keys(place).length > 0) {
        const originChanged = place.slug !== currentOrigin?.slug;
        const shouldClearDestination =
          originChanged && currentDestination && currentDestination.slug && !destinationFirst; // NEW: Don't clear in experimental

        if (shouldClearDestination) {
          // Normal flow (control): clear destination
          this.setState({
            destination: {},
            destinationResetKey: destinationResetKey + 1,
            clearedDestinationSlug: true,
          });

          updateMaxDaysSearch(null);
          this.validatePendingDates();
        } else if (
          originChanged &&
          currentDestination &&
          currentDestination.slug &&
          destinationFirst
        ) {
          const destinationMaxDaysSearch = place.meta?.maxDaysSearch || null;
          updateMaxDaysSearch(destinationMaxDaysSearch);
          this.validatePendingDates(destinationMaxDaysSearch);

          setTimeout(() => {
            this.openDepartureCalendar();
          }, 0);
        }
      }

      // When clearing a field (origin or destination)
      if (Object.keys(place).length === 0) {
        // Clear dates only if origin is cleared
        if (field === 'origin') {
          this.compactDateUsed = false;
          this.clearReturnDate();
          this.clearDepartureDate();
        }

        // Reset completed pair flag in both cases (origin or destination)
        if (destinationFirst) {
          this.bothPlacesWereSelected = false;
        }
      }

      if (field === 'destination' && Object.keys(place).length) {
        const destinationMaxDaysSearch = place.meta?.maxDaysSearch || null;

        updateMaxDaysSearch(destinationMaxDaysSearch);
        this.validatePendingDates(destinationMaxDaysSearch);

        // CRITICAL: Use setTimeout to ensure it executes after BOTH setState calls
        setTimeout(() => {
          this.openDepartureCalendar();
        }, 0);

        // Track destination selected first in experimental group
        const originIsEmpty = !this.state.origin?.slug;
        if (destinationFirst && originIsEmpty) {
          mixpanelTracker.trackEvent('Destination Selected First', {
            destination: place.slug,
          });
        }
      }

      if (errors[field] && Object.keys(place).length > 0) {
        this.setState(prevState => ({
          errors: {
            ...prevState.errors,
            [field]: '',
          },
        }));
      }
    }
  }

  /**
   * Validates and formats a date if it's valid
   * @param {string} date - The date to validate
   * @param {number} maxDaysSearch - Maximum days allowed for search
   * @returns {string|null} Formatted date or null if invalid
   */
  validateAndFormatDate(date, maxDaysSearch) {
    if (!date) return null;

    const {
      openTicketText,
      config: { dateFormat },
    } = this.props;

    const isValid = validateDate(date, maxDaysSearch, openTicketText, dateFormat);
    if (!isValid) {
      console.warn('[VALIDATION] Date validation failed:', { date, maxDaysSearch });
      return null;
    }

    if (date === openTicketText) return date;

    return this.formatDateForDisplay(date, dateFormat);
  }

  /**
   * Updates datepickers after date changes and triggers event handlers
   * to establish proper date constraints between departure and return fields.
   *
   * This ensures that when two dates are set (initial and final), the departure
   * date picker is limited by the return date maximum, and the return date picker
   * is limited by the departure date minimum. Without this method, users could
   * select invalid date combinations.
   *
   * @param {string|null} departureDate - Departure date
   * @param {string|null} returnDate - Return date
   */
  updateDatePickersAfterChange(departureDate, returnDate) {
    if (!this.$get) {
      console.warn('[DATEPICKER] $get not available for datepicker updates');
      return;
    }

    if (departureDate) {
      this.$get('returnDate').trigger('changeDepartureDate', departureDate);
    }
    if (returnDate) {
      this.$get('departureDate').trigger('changeReturnDate', returnDate);
    }
  }

  /**
   * Updates datepicker ranges after validation to enforce correct constraints
   * @param {number} maxDaysSearchToUse - The validated maxDaysSearch value to enforce
   */
  updateDatepickerRangesAfterValidation(maxDaysSearchToUse) {
    if (!this.$get || !this.departurePicker) {
      console.warn('[DATEPICKER] Datepickers not ready for range validation');
      return;
    }

    const { min, max } = getCalendarRanges(maxDaysSearchToUse);

    this.departurePicker.pickadate('picker').set('min', min, { muted: true });
    this.departurePicker.pickadate('picker').set('max', max, { muted: true });

    const returnPicker = this.$get('returnDate');
    if (returnPicker.length) {
      returnPicker.pickadate('picker').set('min', min, { muted: true });
      returnPicker.pickadate('picker').set('max', max, { muted: true });
    }
  }

  /**
   * Updates the visual input values to match the validated state
   */
  updateInputValuesAfterValidation() {
    const { departureDate, returnDate } = this.state;

    if (this.$get('returnDate').length) {
      this.$get('returnDate').val(returnDate || '');
    }

    if (this.$get('departureDate').length) {
      this.$get('departureDate').val(departureDate || '');
    }
  }

  /**
   * Validates pending dates when destination metadata is available
   * @param {number|null} destinationMaxDaysSearch - The maxDaysSearch value for the destination
   */
  validatePendingDates(destinationMaxDaysSearch) {
    const { config } = this.props;
    const { initialMaxDaysSearch } = config;
    const {
      _needsDateValidation,
      _pendingDepartureDate,
      _pendingReturnDate,
      departureDate,
      returnDate,
    } = this.state;

    const maxDaysSearchToUse = destinationMaxDaysSearch || initialMaxDaysSearch;

    if (!_needsDateValidation) {
      this.handleExistingDatesValidation(departureDate, returnDate, maxDaysSearchToUse);
      return;
    }

    const validatedDepartureDate = this.validateAndFormatDate(
      _pendingDepartureDate,
      maxDaysSearchToUse
    );
    const validatedReturnDate = this.validateAndFormatDate(_pendingReturnDate, maxDaysSearchToUse);

    this.setState(
      {
        departureDate: validatedDepartureDate,
        returnDate: validatedReturnDate,
        _needsDateValidation: false,
        _pendingDepartureDate: undefined,
        _pendingReturnDate: undefined,
      },
      () => {
        // Update datepicker ranges to reflect the constraints
        this.updateDatepickerRangesAfterValidation(maxDaysSearchToUse);

        // Update datepickers and trigger event handlers
        this.updateDatePickersAfterChange(validatedDepartureDate, validatedReturnDate);

        this.updateInputValuesAfterValidation();
      }
    );
  }

  /**
   * Handles validation of existing dates when maxDaysSearch changes
   * @param {string|null} departureDate - Current departure date
   * @param {string|null} returnDate - Current return date
   * @param {number} maxDaysSearchToUse - New maxDaysSearch limit
   */
  handleExistingDatesValidation(departureDate, returnDate, maxDaysSearchToUse) {
    const hasSelectedDates = departureDate || returnDate;

    if (!hasSelectedDates) {
      return;
    }

    // Validate existing dates against the new limit
    const validatedDepartureDate = this.validateAndFormatDate(departureDate, maxDaysSearchToUse);
    const validatedReturnDate = this.validateAndFormatDate(returnDate, maxDaysSearchToUse);
    const hasInvalidDates = !validatedDepartureDate || !validatedReturnDate;

    if (hasInvalidDates) {
      // Clear invalid dates and update UI
      const newState = {};

      if (!validatedDepartureDate) {
        newState.departureDate = null;
      }
      if (!validatedReturnDate) {
        newState.returnDate = null;
      }

      this.setState(newState);

      /**
       * Note: setTimeout is used here because when maxDays changes, it causes
       * the datepicker fields to refresh due to the key change. Without this
       * timeout, there are rare occasions where the update operations happen
       * simultaneously and the datepicker doesn't get updated properly.
       * The timeout ensures the datepicker has time to refresh before we
       * attempt to update its values and ranges.
       */
      setTimeout(() => {
        this.updateDatePickersAfterChange(validatedDepartureDate, validatedReturnDate);
        this.updateInputValuesAfterValidation();
      }, 100);
    }
  }

  onChangeDepartureToOpenTicket() {
    const { hybridTrip } = this.state;
    if (hybridTrip === 'none') {
      this.setState({ hybridTrip: 'departure' });
    } else if (hybridTrip === 'return') {
      this.setState({ hybridTrip: 'both' });
    }
  }

  onChangeReturnToOpenTicket() {
    const { hybridTrip } = this.state;
    if (hybridTrip === 'none') {
      this.setState({ hybridTrip: 'return' });
    } else if (hybridTrip === 'departure') {
      this.setState({ hybridTrip: 'both' });
    }
  }

  onRemoveReturnOpenTicket() {
    const { hybridTrip } = this.state;
    if (hybridTrip === 'both') {
      this.setState({ hybridTrip: 'departure' });
    } else if (hybridTrip === 'return') {
      this.setState({ hybridTrip: 'none' });
    }
  }

  onRemoveDepartureOpenTicket() {
    const { hybridTrip } = this.state;
    if (hybridTrip === 'both') {
      this.setState({ hybridTrip: 'return' });
    } else if (hybridTrip === 'departure') {
      this.setState({ hybridTrip: 'none' });
    }
  }

  onChangeDepartureDate(departureDate, isUnified = false) {
    const { openTicketText, config } = this.props;
    const { allowHybridTrip } = config;
    const isOpenDate = departureDate === openTicketText;
    this.setState({ departureDate }, () => this.setGtmProperties());

    if (!isOpenDate) {
      this.onRemoveDepartureOpenTicket();
    }

    // If hybrid trip is not allowed, but departure is open date
    // Then return date should be cleared and disabled
    if (!allowHybridTrip && isOpenDate) {
      if (isUnified) {
        this.setState({ returnDate: '' });
        this.onRemoveReturnOpenTicket();
      } else {
        this.clearReturnDate();
      }
    }

    if (!isUnified && departureDate !== '') {
      this.$get('returnDate').trigger('changeDepartureDate', departureDate);
    }

    const { errors } = this.state;
    if (errors.departureDate && departureDate !== '') {
      this.setState(prevState => ({
        errors: {
          ...prevState.errors,
          departureDate: '',
        },
      }));
    }

    if (!isUnified && departureDate) {
      this.autoOpenPassengersSelector();
    }
  }

  clearDepartureDate() {
    const event = $.Event('clearDate');
    event.keepCalendarClosed = true;
    this.$get('departureDate').trigger(event);
  }

  clearReturnDate() {
    this.setState({ returnDate: '' });
    this.onRemoveReturnOpenTicket();
    this.$get('returnDate').trigger('clearDate');
  }

  onDepartureDateSelected(selectionType) {
    this.calendarButtonUsed = selectionType;
  }

  onChangeReturnDate(returnDate, isUnified = false) {
    const {
      openTicketText,
      config: { maxDaysSearch },
    } = this.props;
    this.setState({ returnDate }, () => this.setGtmProperties());

    if (returnDate !== openTicketText) {
      this.onRemoveReturnOpenTicket();
    }

    if (!isUnified && returnDate !== '') {
      this.$get('departureDate').trigger('changeReturnDate', returnDate);
    }

    // If the return date is null, it means that the return is not set, and the departure limit is removed
    if (!isUnified && !returnDate && this.departurePicker) {
      const { max } = getCalendarRanges(maxDaysSearch);
      this.departurePicker.pickadate('picker').set('max', max);
    }

    const { errors } = this.state;
    if (errors.returnDate && returnDate !== '') {
      this.setState(prevState => ({
        errors: {
          ...prevState.errors,
          returnDate: '',
        },
      }));
    }
  }

  onChangePassengers(type, quantity) {
    this.setState(
      prevState => ({
        passengers: {
          ...prevState.passengers,
          [type]: quantity,
        },
      }),
      () => this.setGtmProperties()
    );
  }

  onResetPassengers({ passengers }) {
    const newPassengersValues = passengers.reduce((acc, passenger) => {
      acc = {
        ...acc,
        [passenger.name]: passenger.value,
      };
      return acc;
    }, {});

    this.setState({
      passengers: newPassengersValues,
    });
  }

  handleOnClickSubmit() {
    const { config } = this.props;
    const { mobilityEnabled, mobilityDefaultSelected } = config;
    const { mobilitySelected } = this.state;

    mixpanelTracker.trackEvent('Search Clicked', {
      mobilityEnabled: Boolean(mobilityEnabled),
      mobilitySelected: Boolean(mobilitySelected),
      mobilityLeaveBehind: shouldOpenMobilityLeaveBehind({
        mobilityEnabled,
        mobilitySelected,
      }),
      mobilityDefaultSelected: Boolean(mobilityDefaultSelected),
    });
    this.onSubmit();
  }

  onMobilityCheckboxChange({ target }) {
    const { mobilityDefaultSelected } = this.props.config;
    const mobilitySelected = target.checked;

    mixpanelTracker.trackEvent('Mobility Checkbox Toggled', {
      selected: mobilitySelected,
      defaultSelected: Boolean(mobilityDefaultSelected),
    });

    this.setState({ mobilitySelected });
  }

  renderMobilityExtension() {
    const { mobilityEnabled } = this.props.config;
    const { mobilitySelected } = this.state;

    if (!mobilityEnabled) return null;

    return (
      <div className="mobility-leave-behind">
        <label htmlFor="mobility-booking-checkbox">
          <input
            type="checkbox"
            id="mobility-booking-checkbox"
            checked={mobilitySelected}
            onChange={this.onMobilityCheckboxChange}
          />
          <Text id="label.mobility_booking" />
        </label>
      </div>
    );
  }

  setSeatsOnResultQueryParam(url) {
    const { growthbookFeatures } = this.props;
    const seatsOnResultsActive = growthbookFeatures?.results_seats;
    if (!seatsOnResultsActive) return url;
    const seatsOnResultsType = growthbookFeatures?.enable_categories;
    switch (seatsOnResultsType) {
      case 'simple':
        return this.setUrlParam(url, 'seatsOnResults', 'true');
      case 'categories':
        return this.setUrlParam(url, 'seatsOnResultsDiscounts', 'true');
      default:
        return url;
    }
  }

  onSubmit() {
    if (!this.validate()) return;

    const { config, growthbookFeatures } = this.props;
    const { origin, destination } = this.state;
    const { passengersForSelector } = config;

    const searchType = config.secondRedirectEnabled ? 'secondRedirect' : 'bus';

    const funnelUrl = growthbookFeatures?.funnel_version?.funnelUrl;

    if (funnelUrl) {
      config.funnelUrl = funnelUrl;
    }

    const searchFields = {
      ...this.state,
      useCustomPassengers: !!passengersForSelector?.length,
    };

    const url = resultsUrl(searchFields, config, searchType);

    const { openNewWindow, forceHttps, lang, maxRecentSearches, filters } = config;

    let urlFormatted = `${window.location.protocol}${url}`;
    if (forceHttps) urlFormatted = `https:${url}`;

    urlFormatted = this.getAnalyticsParams(urlFormatted);
    urlFormatted = this.getAnalyticsABParams(urlFormatted);

    if (lang) urlFormatted = this.setUrlParam(urlFormatted, 'lang', lang);

    if (filters) urlFormatted = this.setUrlParam(urlFormatted, 'filters', filters);

    if (maxRecentSearches)
      saveRecentSearches(origin, destination, config.maxRecentSearches, config.recentSearchesLS);

    urlFormatted = this.setSeatsOnResultQueryParam(urlFormatted);

    const { mobilityEnabled, mobilityAid } = config;
    const { mobilitySelected, tripType, returnDate, departureDate: stateDepartureDate } =
      this.state;

    if (
      shouldOpenMobilityLeaveBehind({ mobilityEnabled, mobilitySelected })
    ) {
      console.info('[Mobility leave-behind] onSubmit branch', {
        funnelUrl: config.funnelUrl,
        busUrlPreview: urlFormatted,
        hostHref: window.location.href,
        triggerSearchEvent: config.triggerSearchEvent,
        openNewWindow: config.openNewWindow,
      });

      const mobilityUrl = buildMobilityUrlFromSearch({
        origin,
        destination,
        departureDate: stateDepartureDate,
        returnDate,
        tripType,
        dateFormat: config.dateFormat,
        aid: mobilityAid,
      });

      if (mobilityUrl) {
        mobilitySearchHotelsClickTracker({ aid: mobilityAid });
        const leaveBehindOk = openMobilityLeaveBehind({ busUrl: urlFormatted, mobilityUrl });
        console.info('[Mobility leave-behind] onSubmit complete', { leaveBehindOk, mobilityUrl });
        return;
      }

      console.warn('[Mobility leave-behind] mobilityUrl is null — falling through to normal submit');
    }

    const { triggerSearchEvent } = config;
    if (triggerSearchEvent) {
      const searchEvent = new CustomEvent('SearchWidget:search', {
        detail: {
          absoluteSearchUrl: urlFormatted,
        },
      });
      window.dispatchEvent(searchEvent);
      return;
    }

    if (openNewWindow) {
      window.open(urlFormatted);
    } else {
      window.top.location.href = urlFormatted;
    }
  }

  setUrlParam(url, paramName, param) {
    const newUrl = new URL(url);
    newUrl.searchParams.append(paramName, param);
    return newUrl.toString();
  }

  getAnalyticsABParams(url) {
    const { config } = this.props;
    const { destination } = this.state;
    const { abtestDestinationHit, abtestSelectedTripType } = config;

    // If every item in the array is false, then return url
    if ([abtestDestinationHit, abtestSelectedTripType].every(item => !item)) {
      return url;
    }

    const prefix = 'analytics_';

    const newUrl = new URL(url);
    if (abtestSelectedTripType)
      newUrl.searchParams.append(`${prefix}round_trip_ab`, this.abRoundTrip);

    newUrl.searchParams.append(`${prefix}destination_hit_order_ab`, this.abDestinationHit);
    if (abtestDestinationHit && !this.abDestinationHit) return newUrl.toString();

    newUrl.searchParams.append(`${prefix}destination_hit_order`, destination.position);

    return newUrl.toString();
  }

  getAnalyticsParams(url) {
    const { config } = this.props;
    const { origin, geoLocationOrigin: geolocationOriginPlace } = this.state;
    const { geolocationOrigin, showNearestTerminal } = config;
    const prefix = 'analytics_';
    const newUrl = new URL(url);

    if (geolocationOrigin) {
      newUrl.searchParams.append(`${prefix}geo_place_used`, this.usingGeoOrigin);
      newUrl.searchParams.append(`${prefix}geo_place_showed`, this.geoOriginShowed);
    }

    const nearestTerminalShowed = Boolean(showNearestTerminal && geolocationOriginPlace);
    if (nearestTerminalShowed) {
      newUrl.searchParams.append(`${prefix}nearby_terminal_showed`, nearestTerminalShowed);
      newUrl.searchParams.append(
        `${prefix}nearby_terminal_used`,
        origin.id === geolocationOriginPlace?.id
      );
    }
    this.setPreferredTerminalsFlag();
    const {
      showed: preferredTerminalsShowed,
      usingOrigin: usingPreferredOrigin,
      usingDestination: usingPreferredDestination,
      usingRecentSearch,
    } = this.preferredTerminalsFlags;

    newUrl.searchParams.append(`${prefix}preferred_terminal_showed`, preferredTerminalsShowed);
    newUrl.searchParams.append(`${prefix}recent_search_used`, usingRecentSearch);

    const recentSearches =
      config.maxRecentSearches &&
      getRecentSearches(config.maxRecentSearches, config.recentSearchesLS);
    newUrl.searchParams.append(`${prefix}recent_search_showed`, Boolean(recentSearches.length));

    newUrl.searchParams.append(
      `${prefix}popular_destinations_design_used`,
      this.popularDestinationsUsed
    );

    newUrl.searchParams.append(`${prefix}calendar_button_used`, this.calendarButtonUsed);

    if (preferredTerminalsShowed) {
      newUrl.searchParams.append(
        `${prefix}preferred_terminal_used`,
        Boolean(usingPreferredOrigin || usingPreferredDestination)
      );
    }
    return newUrl.toString();
  }

  setGtmProperties() {
    const { config } = this.props;
    const { origin = {}, destination = {}, departureDate, returnDate, passengers } = this.state;

    if (!isPlaceReady(origin) || !isPlaceReady(destination)) {
      return;
    }

    if (config.useGtm) {
      gtmSetDataLayer({
        originCity: origin.cityName,
        originState: origin.state,
        originCountry: origin.country,
        destinationCity: destination.cityName,
        destinationState: destination.state,
        destinationCountry: destination.country,
        startDate: stringToDate(departureDate),
        endDate: stringToDate(returnDate),
        travelers: passengers,
      });
    }
  }

  getMixpanelSearchLoadedProps() {
    const { config } = this.props;
    const { destinationFirst } = config;
    const {
      abtestDestinationHit,
      abtestSelectedTripType,
      paymentBenefitsAB,
      discountsAB,
      textPromAB,
    } = config;

    return {
      ...(config.siteVersion && {
        'Home Version': config.siteVersion,
      }),
      ...(abtestDestinationHit && {
        'Destination Hit Order Ab Result': this.abDestinationHit ? 'a' : 'b',
      }),
      ...(abtestSelectedTripType && {
        'Round Trip Ab Result': this.abRoundTrip ? 'a' : 'b',
      }),
      ...(paymentBenefitsAB && {
        'Search Payment Methods Ab Result': this.showPaymentMethods ? 'a' : 'b',
      }),
      ...(discountsAB && {
        'Search Discounts Ab Result': this.getSearchDiscountsABResult(),
      }),
      ...(textPromAB && {
        'Search TextProm Ab Result': this.showTextPromAB ? 'a' : 'b',
      }),
      ...(destinationFirst !== undefined && {
        'Destination First AB Result': destinationFirst ? 'experimental' : 'control',
      }),
    };
  }

  getSearchDiscountsABResult() {
    const { config } = this.props;
    const { discountSingle, discountRounded, discountOpenTicket } = config;

    if (!this.showDiscountsAB) return 'b';
    if (discountSingle && discountRounded && discountOpenTicket) return 'c';
    return 'a';
  }

  initAbTests() {
    const { config } = this.props;
    const {
      abtestDestinationHit,
      abtestSelectedTripType,
      paymentBenefitsAB,
      discountsAB,
      textPromAB,
    } = config;

    if (abtestDestinationHit) this.abTestDestinationHit();
    if (abtestSelectedTripType) this.abTestSelectedTripType();
    if (paymentBenefitsAB) this.paymentBenefitsAB();
    if (discountsAB) this.discountsAB();
    if (textPromAB) this.textPromAB();
  }

  abTestDestinationHit() {
    const abResult = initTest(50, 'DESTINATION-HIT-AB');
    this.abDestinationHit = abResult === 'a';
  }

  abTestSelectedTripType() {
    const abResult = initTest(50, 'SELECTED-TRIP-TYPE-AB');
    this.abRoundTrip = abResult === 'a';

    this.setState({ tripType: this.abRoundTrip ? 'round' : 'oneWay' });
  }

  paymentBenefitsAB() {
    const abResult = initTest(50, 'paymentBenefitsAB');
    this.showPaymentMethods = abResult === 'a';
  }

  discountsAB() {
    const abResult = initTest(50, 'discountsAB');
    this.showDiscountsAB = abResult === 'a';
  }

  textPromAB() {
    const abResult = initTest(50, 'textPromAB');
    this.showTextPromAB = abResult === 'a';
  }

  emitSearchLoaded() {
    const { config } = this.props;
    const { interestEvent } = config;
    const mixpanelData = this.getMixpanelSearchLoadedProps();
    mixpanelTracker.trackEvent(interestEvent, mixpanelData);
  }

  getInitialPassengersState() {
    const { config, growthbookFeatures } = this.props;
    const {
      passengersForSelector,
      passengersValues: propPassengersValues,
      passengersDropdown,
      passengers,
    } = config;
    const { passengers: qPassengers } = searchFromQuery();

    let customPassengers;
    if (growthbookFeatures?.search_passengers && passengersDropdown && passengersForSelector) {
      customPassengers = getPassengersSelectorInitialState({
        passengers: passengersForSelector,
        searchValues: propPassengersValues,
      });
    }

    const qPassengersDecoded = decodePassengers(qPassengers || passengers);
    return customPassengers || qPassengersDecoded;
  }

  setInitialState() {
    const { config, openTicketText } = this.props;
    const {
      maxDaysSearch,
      accentColor,
      buttonColor,
      buttonShadow,
      buttonBg,
      departureDate,
      returnDate,
      destination,
      origin,
      dateFormat,
      freezeInitial,
      destinationFirst,
      mobilityDefaultSelected,
      mobilityLabelColor,
    } = config;
    const {
      departureDate: qDepartureDate,
      destination: qDestination,
      origin: qOrigin,
      returnDate: qReturnDate,
    } = searchFromQuery();

    const initialDeparture = qDepartureDate || departureDate;
    const initialDestination = { slug: qDestination || destination };
    const initialOrigin = { slug: qOrigin || origin };
    const initialPassengers = this.getInitialPassengersState();
    const initialReturn = qReturnDate || returnDate;
    const initialDepartureIsOpenTicket = initialDeparture === openTicketText;
    const initialReturnIsOpenTicket = initialReturn === openTicketText;

    const hasInitialDates = initialDeparture || initialReturn;
    const hasDestinationSlug = qDestination || destination;
    const shouldValidateDatesLater = hasInitialDates && hasDestinationSlug;

    // CRITICAL: If there are initial origin AND destination, mark that the pair was already completed
    // This prevents the calendar from opening automatically on initial load
    const hasInitialOriginAndDestination = initialOrigin.slug && initialDestination.slug;
    if (destinationFirst && hasInitialOriginAndDestination) {
      this.bothPlacesWereSelected = true;
    }

    let finalDepartureDate = null;
    let finalReturnDate = null;

    if (shouldValidateDatesLater) {
      // Store formatted dates for display, validate later with destination metadata
      finalDepartureDate = initialDepartureIsOpenTicket
        ? initialDeparture
        : this.formatDateForDisplay(initialDeparture, dateFormat);
      finalReturnDate = initialReturnIsOpenTicket
        ? initialReturn
        : this.formatDateForDisplay(initialReturn, dateFormat);
    } else {
      // Validate immediately with default maxDaysSearch
      finalDepartureDate = this.validateAndFormatDate(initialDeparture, maxDaysSearch);
      finalReturnDate = this.validateAndFormatDate(initialReturn, maxDaysSearch);
    }

    this.setState({
      departureDate: finalDepartureDate,
      destination: initialDestination,
      origin: initialOrigin,
      passengers: initialPassengers,
      returnDate: finalReturnDate,
      originSearchAttempts: [],
      destinationSearchAttempts: [],
      freezedDestination: initialDestination?.slug && freezeInitial,
      freezedOrigin: initialOrigin?.slug && freezeInitial,
      /**
       * Note: Dates are validated after state initialization
       * because some "places" receive dynamic maxDays from the server.
       * If we validated dates at the beginning (during page load or reload),
       * the datepicker fields would be emptied since we wouldn't have
       * the correct maxDaysSearch information to validate against.
       *
       * Therefore, we store initial dates without validation and validate them
       * once we have access to the selected destination's metadata.
       */
      ...(shouldValidateDatesLater && {
        _pendingDepartureDate: initialDeparture,
        _pendingReturnDate: initialReturn,
        _needsDateValidation: true,
      }),
      mobilitySelected: Boolean(mobilityDefaultSelected),
    }, () => this.setGtmProperties());

    // Set hybrid trip state based on open ticket flags
    let hybridTrip = 'none';
    if (initialDepartureIsOpenTicket && initialReturnIsOpenTicket) {
      hybridTrip = 'both';
    } else if (initialDepartureIsOpenTicket) {
      hybridTrip = 'departure';
    } else if (initialReturnIsOpenTicket) {
      hybridTrip = 'return';
    }
    this.setState({ hybridTrip });

    // Update datepickers with initial dates
    this.updateDatePickersAfterChange(departureDate, returnDate);

    // Set CSS properties
    this.rootEl.style.setProperty('--accent-500', accentColor);
    this.rootEl.style.setProperty('--button-color', buttonColor);
    this.rootEl.style.setProperty('--button-shadow', buttonShadow);
    this.rootEl.style.setProperty('--button-bg', buttonBg);
    this.rootEl.style.setProperty('--mobility-checkbox-accent', buttonBg || accentColor);

    if (mobilityLabelColor && MOBILITY_LABEL_COLORS[mobilityLabelColor]) {
      this.rootEl.style.setProperty(
        '--mobility-label-color',
        MOBILITY_LABEL_COLORS[mobilityLabelColor]
      );
    } else {
      this.rootEl.style.removeProperty('--mobility-label-color');
    }
  }

  /**
   * Formats a date for display without validation
   * @param {string} date - Date to format
   * @param {string} dateFormat - Date format
   * @returns {string|null} Formatted date or null
   */
  formatDateForDisplay(date, dateFormat) {
    if (!date) return null;
    const { lang } = this.props.config;
    return dayjs(stringToDate(date, dateFormat))
      .locale(lang.toLowerCase())
      .format(dateFormat.toUpperCase());
  }

  validate() {
    const { maxPassengers } = this.props.config;
    const currentState = this.state;
    const errors = validate(currentState, { maxPassengers });
    this.setState({ errors });

    return Object.keys(errors).length === 0;
  }

  $get(fieldName) {
    return $(this.rootEl).find(this.fieldClasses[fieldName]);
  }

  setDeparturePicker(picker) {
    this.departurePicker = picker;
  }

  reversePlaces() {
    const { origin, destination } = this.state;
    const { updateMaxDaysSearch } = this.props.config;
    const newDestinationMaxDaysSearch = origin?.meta?.maxDaysSearch || null;

    if (origin.slug && destination.slug) {
      this.setState(prev => ({
        origin: prev.destination,
        destination: prev.origin,
        freezedDestination: prev.freezedOrigin,
        freezedOrigin: prev.freezedDestination,
      }));

      // Update context with new destination maxDaysSearch when reversing places
      updateMaxDaysSearch(newDestinationMaxDaysSearch);
      this.validatePendingDates(newDestinationMaxDaysSearch);
    }
  }

  getAutoCompleteUrl() {
    const { config, growthbookFeatures } = this.props;
    const { sourceUrl, line, airline, transporter, forceHttps, filters } = config;
    const sourceUrlToUse = growthbookFeatures?.funnel_version?.sourceUrl || sourceUrl;
    const protocol = window.location.protocol.includes('https') || forceHttps ? 'https:' : 'http:';
    const autocompleteUrl = createUrl(`${protocol}//${sourceUrlToUse}`);
    if (line) {
      autocompleteUrl.setQueryParam('line', line);
    } else if (airline) {
      autocompleteUrl.setQueryParam('airline', airline);
    } else if (transporter) {
      autocompleteUrl.setQueryParam('transporter', transporter);
    }
    if (filters) {
      autocompleteUrl.setQueryParam('filters', filters);
    }
    return autocompleteUrl;
  }

  onFocusSearchButton() {
    const { originSearchAttempts, destinationSearchAttempts } = this.state;
    const lastOriginSearchAttempted = originSearchAttempts.pop();
    const lastDestinationSearchAttempted = destinationSearchAttempts.pop();
    const additionalProps = {
      'Origin Search Attempts': originSearchAttempts,
      'Origin Search String': lastOriginSearchAttempted,
      'Destination Search Attempts': destinationSearchAttempts,
      'Destination Search String': lastDestinationSearchAttempted,
    };
    getInterestInSearchEventHandler('Search Button', additionalProps)();
  }

  onNoSearchResults(field, value) {
    const { origin } = this.state;

    const eventData = {
      'Search string': value,
      'Field': field,
    };

    if (field === 'destination') {
      eventData.Origin = origin?.slug;
    }

    mixpanelTracker.trackEvent('No search results', eventData);
  }

  /**
   * Gets the displayType to use, prioritizing the GrowthBook experiment value
   * @param {string} defaultDisplayType - Default value for displayType
   * @returns {string} The displayType to use
   */
  getDisplayType(defaultDisplayType) {
    const { growthbookFeatures } = this.props;

    // Check if search_widget_display_type experiment exists in GrowthBook
    const experimentValue = growthbookFeatures?.search_widget_display_type;

    // Valid displayType values according to displayTypes.js
    const validDisplayTypes = ['city', 'city_state', 'terminal_city', 'state', 'only_terminal'];

    if (experimentValue && validDisplayTypes.includes(experimentValue)) {
      return experimentValue;
    }

    // If experiment doesn't exist or has invalid value, use default value
    return defaultDisplayType;
  }

  render(props, state) {
    const { config, buttonSearch, growthbookFeatures } = this.props;
    const {
      origin,
      tripType,
      errors,
      passengers,
      departureDate,
      returnDate,
      destination,
      geoLocationOrigin,
      coords,
      preferredOrigin,
      preferredDestination,
      autoOpenCalendar,
      autoOpenPassengersSelector,
      freezedDestination,
      freezedOrigin,
      destinationResetKey,
      clearedDestinationSlug,
    } = state;

    const {
      flatVariant,
      Autocomplete,
      calendarOpen,
      allowHybridTrip,
      passengersDropdown,
      optInReturn,
      discountSingle,
      discountRounded,
      discountOpenTicket,
      redBadge,
      showOpenTicket,
      brandingCopy,
      widgetTitle,
      showErrors,
      displayType,
      origin: defaultOrigin,
      destination: defaultDestination,
      abtestDestinationHit,
      cardsAvailable,
      oxxoAvailable,
      paypalAvailable,
      coppelpayAvailable,
      departureDatePickerText,
      returnDatePickerText,
      dateFormat,
      whatsappUrl,
      whatsappVariant,
      contrast,
      reverseVariant,
      allowFetch,
      showNearestTerminal,
      groupPlaces,
      geolocationOrigin: geoLocationOriginFeature,
      maxPassengers,
      passengersForSelector,
      seasonPassengersForSelector,
      buttonBg,
      maxDaysBulkSearch,
      isBulkTicket,
      maxDaysSearch,
      destinationFirst,
      hideSwapRoutesButton,
      allowedSlugs,
      mobilityEnabled,
    } = config;

    const isVerticalLayout = config.widgetLayout === 'vertical-search';
    const showMobilityUnderRoutes = mobilityEnabled && !isVerticalLayout;
    const showMobilityAboveButton = mobilityEnabled && isVerticalLayout;

    const showWhatsappCTA = growthbookFeatures && growthbookFeatures[`whatsapp_cta`] && whatsappUrl;

    // A round trip is allowed only if both origin and destination allow it. This prevents issues when reversing routes.
    const isRoundTripAllowed =
      (origin?.meta?.roundTripAllowed ?? true) && (destination?.meta?.roundTripAllowed ?? true);
    let hideReturn = optInReturn && tripType === 'oneWay';
    let showOpenTicketCTAOnReturn = allowHybridTrip && calendarOpen;
    let showDepartureOpenButton = calendarOpen;
    const maxDaysSearchToUse = isBulkTicket ? maxDaysBulkSearch : maxDaysSearch;

    const showMundialButton = config.showWorldCupButton;
    const worldCupButtonIcon = config.worldCupButtonIcon;

    // Unified picker is the default; legacy renders only behind the explicit
    // legacy_date_picker GrowthBook flag (kill-switch). Any falsy/undefined
    // value — including GrowthBook not being loaded — resolves to unified.
    const showUnifiedPicker = !growthbookFeatures?.legacy_date_picker;

    if (!calendarOpen && optInReturn && tripType === 'oneWay') hideReturn = true;

    if (allowHybridTrip && calendarOpen) showOpenTicketCTAOnReturn = true;

    const disableReturnDate = !isRoundTripAllowed && tripType === 'none';

    const autocompleteUrl = this.getAutoCompleteUrl();

    const hasTripSwitch =
      ((showOpenTicket && !calendarOpen) || !hideReturn) && (showOpenTicket || optInReturn);

    const hasPaymentMethodsInTop = computeHasPaymentMethodsInTop({
      showOpenTicket,
      calendarOpen,
      hideReturn,
    });

    const orderDestinationHit = abtestDestinationHit ? this.abDestinationHit : true;
    const showPaymentMethods = cardsAvailable && this.showPaymentMethods;

    const routeError = errors.origin || errors.destination;
    const dateError = errors.departureDate || errors.returnDate;

    const whatsappComponent = showWhatsappCTA && (
      <Fragment>
        {whatsappVariant ? (
          <WhatsappBubble whatsappUrl={whatsappUrl} whatsappCopy="whatsapp_message" />
        ) : (
          <WhatsappCta whatsappUrl={whatsappUrl} whatsappCopy="whatsapp_copy" />
        )}
      </Fragment>
    );

    const paymentMethods = showPaymentMethods && (
      <div className="payment-wrapper">
        <PaymentMethods
          cardsAvailable={cardsAvailable}
          oxxoAvailable={oxxoAvailable}
          paypalAvailable={paypalAvailable}
          coppelpayAvailable={coppelpayAvailable}
        />
      </div>
    );

    const showTopPaymentMethods =
      hasPaymentMethodsInTop && showPaymentMethods && !showWhatsappCTA;

    const discountsProps = this.showDiscountsAB && {
      discountSingle,
      discountRounded,
      discountOpenTicket,
    };

    const textPromPicker = this.showTextPromAB && {
      departureDatePickerText,
      returnDatePickerText,
    };

    const initialOriginSlug =
      (!this.inputChanged &&
        (preferredOrigin?.slug || (geoLocationOriginFeature && geoLocationOrigin?.slug))) ||
      defaultOrigin;

    const initialDestinationSlug = clearedDestinationSlug
      ? null
      : (!this.inputChanged && preferredDestination?.slug) || defaultDestination;

    const hasFooter = whatsappComponent || paymentMethods;
    const showHeaderContainer =
      (hasTripSwitch && config.showTripSwitch) || showWhatsappCTA || showTopPaymentMethods;
    const showReturnDate =
      !hideReturn && (returnDate || departureDate || this.compactDateUsed) && isRoundTripAllowed;
    const showPassengersDropdown = growthbookFeatures?.search_passengers && passengersDropdown;

    // Get the displayType using the function that checks GrowthBook
    const finalDisplayType = this.getDisplayType(displayType);

    const layoutGridRoutes = (
      <div
        className={`layout-grid-routes layout-grid-routes-switch ${
          routeError ? 'container-error' : ''
        }`}
      >
        <div className="form-field origin">
          <Suspense fallback={null}>
            <Autocomplete
              field="origin"
              sourceUrl={autocompleteUrl.href}
              onChange={this.onChangeAutocomplete}
              to={destination && destination.slug}
              initialPlaceSlug={initialOriginSlug}
              place={origin}
              error={Boolean(errors.origin)}
              placeholder="origin"
              label="origin"
              displayType={finalDisplayType}
              onFocus={this.onInputFocus('Origin')}
              onInputChange={this.onInputChange}
              onNoSearchResults={this.onNoSearchResults}
              coordsToOrder={coords}
              nearestTerminal={geoLocationOrigin}
              allowFetch={allowFetch}
              flatVariant={flatVariant}
              showNearestTerminal={showNearestTerminal}
              groupPlaces={groupPlaces}
              disabled={freezedOrigin}
              isDestinationFirstEnabled={destinationFirst}
              allowedSlugs={allowedSlugs}
            />
          </Suspense>
          {showErrors && <span className="fieldError">{errors.origin}</span>}
        </div>
        <div className="form-field-border" />
        <div className="form-field destination">
          <Suspense fallback={null}>
            <Autocomplete
              key={`autocomplete-destination-${destinationResetKey}`}
              field="destination"
              sourceUrl={autocompleteUrl.href}
              onChange={this.onChangeAutocomplete}
              from={origin && origin.slug}
              to={destination && destination.slug}
              originHasInitialSlug={Boolean(initialOriginSlug)}
              initialPlaceSlug={initialDestinationSlug}
              place={destination}
              error={Boolean(errors.destination)}
              placeholder="destination"
              label="destination"
              displayType={finalDisplayType}
              orderDestinationHit={orderDestinationHit}
              onFocus={this.onInputFocus('Destination')}
              onInputChange={this.onInputChange}
              onNoSearchResults={this.onNoSearchResults}
              coordsToOrder={coords}
              allowFetch={allowFetch}
              didInitialOriginChanged={this.didInitialOriginChanged}
              groupPlaces={groupPlaces}
              disabled={freezedDestination}
              isDestinationFirstEnabled={destinationFirst}
              allowedSlugs={allowedSlugs}
            />
          </Suspense>
          {showErrors && <span className="fieldError">{errors.destination}</span>}
        </div>

        {!hideSwapRoutesButton && (
          <div className="btn-reverse">
            <ReverseRoutesButton
              onReverse={this.reversePlaces}
              reverseVariant={reverseVariant}
            />
          </div>
        )}
      </div>
    );

    return (
      <Fragment>
        <form
          ref={el => (this.rootEl = el)}
          onSubmit={e => e.preventDefault()}
          className="grid-form"
        >
          {brandingCopy && (
            <Suspense fallback={null}>
              <BannerBranding copy={brandingCopy} />
            </Suspense>
          )}

          {widgetTitle && (
            <div className="title-wrapper">
              <WidgetTitle copy={widgetTitle} />
            </div>
          )}

          {showHeaderContainer && (
            <div className="top-wrapper">
              {hasTripSwitch && config.showTripSwitch && !showUnifiedPicker && (
                <div className="switch">
                  <Suspense fallback={null}>
                    <RoundSwitch
                      value={tripType}
                      onChange={newTripType => this.setState({ tripType: newTripType })}
                      showOpenTicket={showOpenTicket}
                      optInReturn={optInReturn}
                      {...discountsProps}
                      redBadge={redBadge}
                    />
                  </Suspense>
                </div>
              )}
              {showWhatsappCTA && <Fragment>{whatsappComponent}</Fragment>}
              {showTopPaymentMethods && <Fragment>{paymentMethods}</Fragment>}
            </div>
          )}

          <div
            className={`search-form search-form-reverse ${
              reverseVariant ? 'variant-reverse' : ''
            } ${contrast ? 'search-form-contrast' : ''} `}
          >
            {showMobilityUnderRoutes ? (
              <div className="mobility-routes-anchor">
                {layoutGridRoutes}
                {this.renderMobilityExtension()}
              </div>
            ) : (
              layoutGridRoutes
            )}

            {showUnifiedPicker ? (
              <UnifiedDatePicker
                departureDate={departureDate}
                returnDate={returnDate}
                origin={origin}
                destination={destination}
                sourceUrl={config.sourceUrl}
                forceHttps={config.forceHttps}
                useAvailability={Boolean(growthbookFeatures?.use_availability)}
                tripType={tripType}
                dateFormat={dateFormat}
                maxDaysSearch={maxDaysSearchToUse}
                locale={this.props.config?.i18nLng}
                openTicketText={this.props.openTicketText}
                showOpenTicket={showOpenTicket}
                flatVariant={flatVariant}
                error={Boolean(dateError)}
                showMundialButton={showMundialButton}
                showTomorrowWithMundial={growthbookFeatures?.show_tomorrow_with_mundial !== false}
                onTripTypeChange={newType => this.setState({ tripType: newType })}
                onDepartureChange={date => this.onChangeDepartureDate(date, true)}
                onReturnChange={date => this.onChangeReturnDate(date, true)}
                onDepartureOpenTicket={this.onChangeDepartureToOpenTicket}
                onReturnOpenTicket={this.onChangeReturnToOpenTicket}
                onFocus={getInterestInSearchEventHandler('Departure Date')}
                onContinue={this.onDateContinue}
                openCalendar={autoOpenCalendar}
                buttonBg={buttonBg}
                hasPassengersModal={showPassengersDropdown}
              />
            ) : (
              <div
                className={`form-field-date-wrap ${tripType === 'openTicket' ? 'hide' : ''} ${
                  !departureDate && !this.compactDateUsed ? 'compact-date' : ''
                }
                  ${dateError ? 'container-error' : ''}`}
              >
                <div className={`form-field-date ${hideReturn ? 'border-radius' : ''}`}>
                  <Localizer>
                    <Suspense fallback={null}>
                      <Datepicker
                        field="departureDate"
                        onSelect={this.onDepartureDateSelected}
                        onClose={this.onChangeDepartureDate}
                        error={Boolean(errors.departureDate)}
                        placeholder="departure_date"
                        maxDaysSearch={maxDaysSearchToUse}
                        calendarOpen={showDepartureOpenButton}
                        onOpenTicketDate={this.onChangeDepartureToOpenTicket}
                        date={departureDate}
                        onFocus={getInterestInSearchEventHandler('Departure Date')}
                        {...textPromPicker}
                        dateFormat={dateFormat}
                        setDeparturePicker={this.setDeparturePicker}
                        way="departure"
                        onDatePickerCreated={this.onDatePickerCreated}
                        autoOpenCalendar={autoOpenCalendar === 'departure'}
                        label="departure_date"
                        returnDate={returnDate}
                        title={<Text id="title.select_departure_date" />}
                        skipInitialValidation={this.state._needsDateValidation}
                        key={`datepicker-${maxDaysSearchToUse}`}
                        showMundialButton={showMundialButton}
                        worldCupButtonIcon={worldCupButtonIcon}
                        originSlug={origin?.slug}
                        destinationSlug={destination?.slug}
                        sourceUrl={config.sourceUrl}
                        forceHttps={config.forceHttps}
                      />
                    </Suspense>
                  </Localizer>
                  {showErrors && <span className="fieldError">{errors.departureDate}</span>}
                </div>

                <div className={`dates-separator ${!showReturnDate ? 'hide' : ''}`} />

                <div className={`form-field-date ${!showReturnDate ? 'hide' : ''}`}>
                  <Localizer>
                    <Suspense fallback={null}>
                      <Datepicker
                        field="returnDate"
                        onClose={this.onChangeReturnDate}
                        error={Boolean(errors.returnDate)}
                        hide={hideReturn}
                        label="return_date"
                        placeholder={`return${!optInReturn || calendarOpen ? '_option' : ''}`}
                        maxDaysSearch={maxDaysSearchToUse}
                        calendarOpen={showOpenTicketCTAOnReturn}
                        onOpenTicketDate={this.onChangeReturnToOpenTicket}
                        date={returnDate}
                        onFocus={getInterestInSearchEventHandler('Return Date')}
                        disabled={disableReturnDate}
                        {...textPromPicker}
                        dateFormat={dateFormat}
                        way="return"
                        departureDate={departureDate}
                        onDatePickerCreated={this.onDatePickerCreated}
                        title={<Text id="title.select_return_date" />}
                        skipInitialValidation={this.state._needsDateValidation}
                        key={`return-datepicker-${maxDaysSearchToUse}`}
                        showMundialButton={showMundialButton}
                        worldCupButtonIcon={worldCupButtonIcon}
                        originSlug={origin?.slug}
                        destinationSlug={destination?.slug}
                        sourceUrl={config.sourceUrl}
                        forceHttps={config.forceHttps}
                      />
                    </Suspense>
                  </Localizer>
                  {showErrors && <span className="fieldError">{errors.returnDate}</span>}
                </div>
              </div>
            )}

            {showPassengersDropdown && (
              <div className="form-field sw-dropdown">
                <PassengersSelector
                  passengersList={passengersForSelector}
                  seasonPassengersList={seasonPassengersForSelector}
                  maxPassengers={maxPassengers}
                  onUpdatePassengers={this.onChangePassengers}
                  error={Boolean(errors.passengers)}
                  passengersSelected={passengers}
                  onClick={getInterestInSearchEventHandler('Passengers')}
                  onSubmit={this.onSubmit}
                  onResetPassengers={this.onResetPassengers}
                  autoOpen={autoOpenPassengersSelector}
                  onDropdownClose={this.onPassengersDropdownClose}
                />
                {showErrors && <span className="fieldError">{errors.passengers}</span>}
              </div>
            )}

            {showMobilityAboveButton && this.renderMobilityExtension()}

            <div className="button-wrapper">
              <button
                type="submit"
                onClick={this.handleOnClickSubmit}
                className={`search-button ${buttonBg ? 'search-button-bg' : ''} ${
                  contrast ? 'search-button-contrast' : ''
                }`}
                ct-submit="true" // Clicktripz
                onFocus={this.onFocusSearchButton}
              >
                <div
                  className="search-icon"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: selectIcon('searchButton', false),
                  }}
                />
                {buttonSearch}
              </button>
            </div>
          </div>

          {/* Coupling: paymentMethods and whatsappComponent are also rendered in
              the top-wrapper when showTopPaymentMethods / showWhatsappCTA is true
              (see top-wrapper JSX above). Duplication is prevented only by the
              `bottom-show-for-small` class hiding this wrapper on large screens
              (see src/styles/layout/_grid.scss). Do NOT remove that class without
              also gating {paymentMethods}/{whatsappComponent} below on the
              negation of the corresponding top-wrapper flag. */}
          {hasFooter && (
            <div
              className={`bottom-wrapper ${(showTopPaymentMethods || showWhatsappCTA) ? 'bottom-show-for-small' : ''} ${
                cardsAvailable ? '' : 'show-center'
              }`}
            >
              {paymentMethods}
              {whatsappComponent}
            </div>
          )}
        </form>
      </Fragment>
    );
  }
}

export default withText({
  buttonSearch: <Text id="button.search" />,
  openTicketText: <Text id={`label.open_ticket`} />,
})(withWidgetConfig(withGrowthBookFeatures(withUserPreferences(Search))));
